import { useState, useEffect, useCallback } from 'react';
import { api, getApiUrl } from '../utils/apiConfig';
import { getUserData } from '../utils/userUtils';

/**
 * Create-form destination id -> channel_types.name, for the destinations where the two
 * differ. Every other platform's destination id IS its channel type name, so this map
 * has exactly one entry and should stay that way: a new channel type is better named
 * after its destination id than added here.
 *
 * Mirror of DESTINATION_IDS_BY_CHANNEL_TYPE (the inverse) in
 * apps/app-backend/utils/permissions.js — keep the two in step.
 */
export const CHANNEL_TYPE_BY_DESTINATION = {
  gam: 'google-ad-manager',
};

/**
 * Custom hook to fetch channel configurations for a specific account
 * Returns full channel configs including channel IDs for selection
 * 
 * @param {string} accountId - The account ID to fetch channel configs for
 * @param {Object} user - User object (for validation)
 * @returns {Object} { channelConfigs, isLoading, error, refetch, getChannelsForPlatform }
 */
const EMPTY_CONFIGS = {};

export const useChannelConfigs = (accountId, user) => {
  const scope = `${accountId || ''}:${user?.id || ''}`;
  const [result, setResult] = useState(null);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let cancelled = false;
    if (!accountId || !user?.id) {
      setResult({ scope, configs: {}, loading: false, error: null });
      return undefined;
    }
    setResult({ scope, configs: {}, loading: true, error: null });
    (async () => {
      try {
        const userId = getUserData()?.userId || user.id;
        const { data } = await api.get(getApiUrl(`/api/users/channel-configs/${accountId}`), { params: { userId } });
        if (!cancelled) setResult({ scope, configs: data.channelConfigs || {}, loading: false, error: null });
      } catch (error) {
        if (!cancelled) setResult({ scope, configs: {}, loading: false, error });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, user?.id, revision]);
  const refetch = useCallback(() => setRevision((value) => value + 1), []);
  const current = result?.scope === scope ? result : null;
  const channelConfigs = current?.configs || EMPTY_CONFIGS;
  const isLoading = current?.loading ?? Boolean(accountId && user?.id);
  const error = current?.error || null;

  /**
   * Get all channel configs for a specific platform
   * @param {string} platform - The platform/channel type (e.g., 'facebook', 'dv360')
   * @returns {Array} Array of channel configs for this platform
   */
  const getChannelsForPlatform = useCallback((platform) => {
    if (!platform || !channelConfigs) return [];

    // Normalize platform name (handle cases like 'facebook' vs 'Facebook')
    const normalizedPlatform = platform.toLowerCase();
    // ...then translate the create-form DESTINATION ID to the CHANNEL TYPE NAME this
    // map is actually keyed by (GET /api/users/channel-configs keys on
    // channel_types.name lowercased — routes/user.js). For every destination but one
    // those are the same string, so this is a no-op; GAM is the exception. Done here
    // rather than at the call sites because CampaignCreation alone passes
    // selectedDestination to these helpers in eleven places, and
    // CampaignLaunchWorkflow passes its own `destination` — each one silently
    // returning [] is what makes the workflow report "no channel configuration on
    // this account" for a channel that is configured.
    const channelTypeKey = CHANNEL_TYPE_BY_DESTINATION[normalizedPlatform] || normalizedPlatform;
    return channelConfigs[channelTypeKey] || [];
  }, [channelConfigs]);

  /**
   * Check if a platform has multiple channel configs (requires user selection)
   * @param {string} platform - The platform/channel type
   * @returns {boolean} True if multiple configs exist
   */
  const hasMultipleChannels = useCallback((platform) => {
    const channels = getChannelsForPlatform(platform);
    return channels.length > 1;
  }, [getChannelsForPlatform]);

  /**
   * Get the default channel ID for a platform (first available)
   * @param {string} platform - The platform/channel type
   * @returns {string|null} The channel ID or null if none available
   */
  const getDefaultChannelId = useCallback((platform) => {
    const channels = getChannelsForPlatform(platform);
    return channels.length > 0 ? channels[0].id : null;
  }, [getChannelsForPlatform]);

  return {
    channelConfigs,
    isLoading,
    error,
    refetch,
    getChannelsForPlatform,
    hasMultipleChannels,
    getDefaultChannelId
  };
};

